/**
 * Runtime Configuration Service
 * Resolves runtime endpoints from the exact verification hostname and loads
 * security-sensitive configuration from an authenticated Portal session.
 */

import { sessionStateManager } from '../utils/SessionStateManager';
import { getLaunchCredential } from '../utils/launchCredentials';
import { getDevBrandSlug } from '../contexts/DevBrandContext';
import type { BrandConfig } from '../contexts/BrandContext';
import {
  detectBrandFromHostname,
  detectEnvironmentFromHostname,
  getBrandUrls,
} from '../utils/brand-urls';

export interface RuntimeConfig {
  apiUrl: string;
  engineUrl: string;
  wsUrl: string;
  internalServiceToken: string;
  environment: 'development' | 'staging' | 'production';
  features: {
    testMode: boolean;
    warmupPeriodMs?: number; // Optional - controlled by SDK
    qualityThreshold: number;
    sandboxMode?: boolean;
  };
  // Session fields returned by authenticated Portal configuration.
  sessionId?: string;
  apiKey?: string; // For analytics authentication
  sessionToken?: string; // For WebSocket authentication
  returnUrl?: string;
  cancelUrl?: string;
  externalUserId?: string;
  // Verification configuration
  verificationMode?: 'L1' | 'L2';
  challengeAge?: number;
  faceMatchEnabled?: boolean;
  // QR handoff support
  handoffToken?: string;
  // Brand configuration from session config response
  brand?: BrandConfig;
}

class RuntimeConfigService {
  private config: RuntimeConfig | null = null;
  private configPromise: Promise<RuntimeConfig> | null = null;

  /**
   * Load configuration from available sources
   * Priority: config.json > window.runtimeConfig > defaults
   */
  async loadConfig(): Promise<RuntimeConfig> {
    // Return cached config if already loaded
    if (this.config) {
      return this.config;
    }

    // Return existing promise if config is being loaded
    if (this.configPromise) {
      return this.configPromise;
    }

    // Start loading config
    this.configPromise = this.loadConfigInternal();
    this.config = await this.configPromise;
    return this.config;
  }

  private async loadConfigInternal(): Promise<RuntimeConfig> {
    // Check for API partner mode first (sessionToken or handoffToken present).
    // The one-time QR handoff token is exchanged only from the live launch URL;
    // once it has been captured and removed from the address bar, refresh
    // continuity comes from the stored session state instead of replaying it.
    const params = new URLSearchParams(window.location.search);
    const sessionToken = getLaunchCredential('sessionToken');
    const handoffToken = params.get('handoffToken');
    
    // Handle QR handoff flow differently
    if (handoffToken) {
      const qrConfig = await this.loadFromHandoffToken(handoffToken);
      if (qrConfig) {
        console.log('[RuntimeConfig] Loaded configuration via QR handoff token');
        return qrConfig;
      }
    }
    
    // Handle regular sessionToken flow
    if (sessionToken) {
      const apiConfig = await this.loadFromSessionToken(sessionToken);
      if (apiConfig) {
        console.log('[RuntimeConfig] Loaded configuration via API partner session token');
        return apiConfig;
      }
    }

    // Check for configuration in restored session state (page refresh scenario)
    const sessionConfig = await this.loadFromSessionState();
    if (sessionConfig) {
      console.log('[RuntimeConfig] Loaded configuration from restored session state');
      return sessionConfig;
    }

    // Fail fast - configuration must be provided via URL parameters, tokens, or session state
    const hasToken = !!(sessionToken || handoffToken);
    const errorMsg = hasToken 
      ? '[RuntimeConfig] Failed to load configuration via token. Check session validity and API connectivity.'
      : '[RuntimeConfig] No configuration found in URL parameters or session state. Verify-UI must be accessed via the SDK.';
    console.error(errorMsg, { hasSessionToken: !!sessionToken, hasHandoffToken: !!handoffToken });
    throw new Error(errorMsg);
  }

  /**
   * Load configuration from handoff token (QR Code Handoff Mode)
   * Uses the QR config endpoint which expects the token as a query parameter
   */
  private async loadFromHandoffToken(
    handoffToken: string
  ): Promise<RuntimeConfig | null> {
    try {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('sessionId');
      
      if (!sessionId) {
        console.error('[RuntimeConfig] Missing sessionId with handoffToken');
        return null;
      }

      // Detect environment from hostname
      const environment = this.detectEnvironment();
      const apiUrl = this.getDefaultApiUrl(environment);
      
      // Build headers with optional dev brand override
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const devBrand = getDevBrandSlug();
      if (devBrand) {
        headers['X-Brand-Override'] = devBrand;
      }

      // Call QR config endpoint with token as query parameter
      const response = await fetch(`${apiUrl}/api/v1/sessions/${sessionId}/qr-config?token=${handoffToken}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        // Parse JSON error response if possible to get detailed message
        let errorDetail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorDetail = errorJson.message || errorJson.error || errorText;
        } catch {
          // Use raw text if not JSON
        }

        const errorMessage = response.status === 404
          ? `Session not found (404): ${sessionId}. Session IDs must be created via /api/v1/sessions/create endpoint.`
          : `Failed to fetch session config (${response.status}): ${errorDetail}`;

        console.error('[RuntimeConfig] Failed to fetch QR config:', {
          status: response.status,
          statusText: response.statusText,
          error: errorDetail,
          url: `${apiUrl}/api/v1/sessions/${sessionId}/qr-config`
        });

        // Throw error to propagate message (including session expiry) to caller
        throw new Error(errorMessage);
      }

      const sessionData = await response.json();
      this.validateServerSessionConfig(sessionData, sessionId);
      console.log('[RuntimeConfig] Successfully fetched QR config:', {
        sessionId: sessionId.substring(0, 8) + '...',
        merchantId: sessionData.merchantId?.substring(0, 8) + '...',
        hasReturnUrl: !!sessionData.returnUrl,
        testMode: sessionData.testMode
      });
      
      // Build config from QR session data
      const config: RuntimeConfig = {
        apiUrl: apiUrl,
        engineUrl: this.getDefaultEngineUrl(environment),
        wsUrl: this.getDefaultWsUrl(environment),
        internalServiceToken: '', // Never expose in frontend
        environment: environment,
        features: {
          testMode: sessionData.testMode || false,
          warmupPeriodMs: 500,
          qualityThreshold: 0.55,
          sandboxMode: sessionData.sandboxMode ?? false,
        },
        sessionId: sessionId,
        sessionToken: sessionData.sessionToken,
        apiKey: sessionData.merchantId,
        returnUrl: sessionData.returnUrl,
        cancelUrl: sessionData.cancelUrl,
        externalUserId: sessionData.externalUserId,
        // Include verification configuration from session
        verificationMode: sessionData.verificationMode,
        challengeAge: sessionData.challengeAge,
        faceMatchEnabled: sessionData.faceMatchEnabled ?? true,
        // Extract brand block from session config (WL-303)
        brand: sessionData.brand,
      };

      return config;
    } catch (error) {
      console.error('[RuntimeConfig] Error loading from handoff token:', error);
      return null;
    }
  }

  /**
   * Load configuration from session token (API Partner Mode)
   * Simple approach - fetch config from backend using sessionToken
   */
  private async loadFromSessionToken(
    sessionToken: string,
    sessionIdOverride?: string,
  ): Promise<RuntimeConfig | null> {
    try {
      const params = new URLSearchParams(window.location.search);
      const sessionId = sessionIdOverride || params.get('sessionId');
      
      if (!sessionId) {
        console.error('[RuntimeConfig] Missing sessionId with sessionToken');
        return null;
      }

      // Detect environment from hostname
      const environment = this.detectEnvironment();
      const apiUrl = this.getDefaultApiUrl(environment);
      
      // Build headers with auth and optional dev brand override
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${sessionToken}`,
      };
      const devBrand = getDevBrandSlug();
      if (devBrand) {
        headers['X-Brand-Override'] = devBrand;
      }

      // Simple API call to get session config
      const response = await fetch(`${apiUrl}/api/v1/sessions/${sessionId}/config`, {
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        // Parse JSON error response if possible to get detailed message
        let errorDetail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorDetail = errorJson.message || errorJson.error || errorText;
        } catch {
          // Use raw text if not JSON
        }

        const errorMessage = response.status === 404
          ? `Session not found (404): ${sessionId}. Session IDs must be created via /api/v1/sessions/create endpoint.`
          : `Failed to fetch session config (${response.status}): ${errorDetail}`;

        console.error('[RuntimeConfig] Failed to fetch session config:', {
          status: response.status,
          statusText: response.statusText,
          error: errorDetail,
          url: `${apiUrl}/api/v1/sessions/${sessionId}/config`
        });

        // Throw error to propagate message (including session expiry) to caller
        throw new Error(errorMessage);
      }

      const sessionData = await response.json();
      this.validateServerSessionConfig(sessionData, sessionId);
      console.log('[RuntimeConfig] Successfully fetched session config:', {
        sessionId: sessionId.substring(0, 8) + '...',
        merchantId: sessionData.merchantId?.substring(0, 8) + '...',
        hasReturnUrl: !!sessionData.returnUrl,
        testMode: sessionData.testMode
      });
      
      // Build config from session data
      const config: RuntimeConfig = {
        apiUrl: apiUrl,
        engineUrl: this.getDefaultEngineUrl(environment),
        wsUrl: this.getDefaultWsUrl(environment),
        internalServiceToken: '', // Never expose in frontend
        environment: environment,
        features: {
          testMode: sessionData.testMode || false,
          warmupPeriodMs: 500,
          qualityThreshold: 0.55,
          sandboxMode: sessionData.sandboxMode ?? false,
        },
        sessionId: sessionId,
        sessionToken: sessionToken,
        apiKey: sessionData.merchantId,
        returnUrl: sessionData.returnUrl,
        cancelUrl: sessionData.cancelUrl,
        externalUserId: sessionData.externalUserId,
        // Include verification configuration from session
        verificationMode: sessionData.verificationMode,
        challengeAge: sessionData.challengeAge,
        faceMatchEnabled: sessionData.faceMatchEnabled ?? true,
        // Include handoffToken for QR code generation
        handoffToken: sessionData.handoffToken,
        // Extract brand block from session config (WL-303)
        brand: sessionData.brand,
      };

      return config;
    } catch (error) {
      console.error('[RuntimeConfig] Error loading from session token:', error);
      return null;
    }
  }

  /**
   * Load configuration from restored session state (page refresh scenario)
   */
  private async loadFromSessionState(): Promise<RuntimeConfig | null> {
    try {
      const sessionState = sessionStateManager.loadState();
      const sessionToken = sessionState?.config?.sessionToken;
      const sessionId = sessionState?.sessionId;
      if (!sessionToken || !sessionId) {
        return null;
      }

      // sessionStorage preserves the server-issued bearer for refresh continuity,
      // but none of its merchant, destination, verification, brand, or endpoint
      // fields are authoritative. Re-resolve those fields from Portal.
      return await this.loadFromSessionToken(sessionToken, sessionId);
    } catch (error) {
      console.error('[RuntimeConfig] Error loading from session state:', error);
      return null;
    }
  }

  private validateServerSessionConfig(
    sessionData: { sessionId?: string; brand?: { brandSlug?: string } },
    requestedSessionId: string
  ): void {
    if (sessionData.sessionId && sessionData.sessionId !== requestedSessionId) {
      throw new Error('Authenticated session configuration did not match the requested session');
    }

    const expectedBrand = detectBrandFromHostname(window.location.hostname);
    const returnedBrand = sessionData.brand?.brandSlug;
    if (expectedBrand && returnedBrand && returnedBrand !== expectedBrand) {
      throw new Error('Authenticated session configuration did not match the verification brand');
    }
  }

  /**
   * Detect environment based on hostname
   */
  private detectEnvironment(): 'development' | 'staging' | 'production' {
    return detectEnvironmentFromHostname(window.location.hostname);
  }

  /**
   * Get default API URL based on environment and brand
   * Uses hostname-based brand detection for correct routing
   */
  private getDefaultApiUrl(
    environment: 'development' | 'staging' | 'production'
  ): string {
    const urls = getBrandUrls(environment);
    return urls.apiUrl;
  }

  /**
   * Get default Engine URL based on environment and brand
   * Uses hostname-based brand detection for correct routing
   */
  private getDefaultEngineUrl(
    environment: 'development' | 'staging' | 'production'
  ): string {
    const urls = getBrandUrls(environment);
    return urls.engineUrl;
  }

  /**
   * Get default WebSocket URL based on environment and brand
   * Uses hostname-based brand detection for correct routing
   */
  private getDefaultWsUrl(
    environment: 'development' | 'staging' | 'production'
  ): string {
    const urls = getBrandUrls(environment);
    return urls.wsUrl;
  }

  /**
   * Get current configuration (throws if not loaded)
   */
  getConfig(): RuntimeConfig {
    if (!this.config) {
      throw new Error('Configuration not loaded. Call loadConfig() first.');
    }
    return this.config;
  }

  /**
   * Get configuration value with fallback
   */
  get<K extends keyof RuntimeConfig>(key: K): RuntimeConfig[K] {
    const config = this.getConfig();
    return config[key];
  }

  /**
   * Check if configuration is loaded
   */
  isLoaded(): boolean {
    return this.config !== null;
  }

  /**
   * Get Portal API URL consistently
   * Helper used by the required artifact handoff client.
   */
  async getPortalApiUrl(): Promise<string | null> {
    try {
      const config = await this.loadConfig();
      return config.apiUrl || null;
    } catch (error) {
      console.error('[RuntimeConfig] Failed to get Portal API URL:', error);
      return null;
    }
  }

  /**
   * Get session ID from config
   * Helper used by the required artifact handoff client.
   */
  getSessionId(): string | null {
    if (!this.config) {
      return null;
    }
    return this.config.sessionId || null;
  }

  /**
   * Reset configuration (useful for testing)
   */
  reset(): void {
    this.config = null;
    this.configPromise = null;
  }
}

// Export singleton instance
export const runtimeConfig = new RuntimeConfigService();

// Export convenience function for getting config
export const getConfig = () => runtimeConfig.getConfig();
